/*
 * Sections appear as the shopper scrolls, like GlowDrop's: when a section comes into view, its parts fade in from
 * invisible with a slight blur that sharpens, one after another. Quicker than GlowDrop's (0.65s, 0.15s apart), as
 * the owner asked: 0.45s each, the first at once and the next 0.08s apart, starting a little earlier. Homepage
 * and product page only (layout/theme.liquid loads this there). The first section (the opening hero, the product
 * itself) is left alone so the page opens at once, and nothing is hidden without this script. The look is in
 * assets/lainly.css.
 *
 * Checked on every scroll (once per frame) rather than with an IntersectionObserver: a jump from above a section
 * to far below it never crosses the screen, and an observer would leave that section hidden. Scrolls are caught on
 * the document, so the desktop page's own scroll box counts too.
 */

const STEP = 0.08;
const MAX_STEPS = 6;
/** A section shows once its top is within this share of the screen's height. */
const TRIGGER = 0.92;

/** The parts of a section that fade in one by one: the children of its first element with more than one child. */
function partsOf(section) {
  let el = section.firstElementChild;
  for (let depth = 0; el && el.children.length === 1 && depth < 3; depth++) el = el.firstElementChild;
  if (!el) return [];
  const parts = [...el.children].filter(
    (child) =>
      child instanceof HTMLElement &&
      !child.matches('script, style, template, dialog, [hidden]') &&
      getComputedStyle(child).position !== 'fixed'
  );
  return parts.length ? parts : [el];
}

function start() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const sections = [...document.querySelectorAll('main .shopify-section')].filter((section) => section.offsetHeight > 0);
  const pending = new Set();

  sections.slice(1).forEach((section) => {
    // Already on screen when the page opens: left as it is.
    if (section.getBoundingClientRect().top < window.innerHeight * TRIGGER) return;
    partsOf(section).forEach((part, index) => {
      part.classList.add('lainly-reveal');
      part.style.setProperty('--lainly-reveal-delay', `${Math.min(index, MAX_STEPS) * STEP}s`);
    });
    pending.add(section);
  });
  if (!pending.size) return;

  let queued = false;
  const check = () => {
    queued = false;
    const line = window.innerHeight * TRIGGER;
    for (const section of pending) {
      if (section.getBoundingClientRect().top < line) {
        section.classList.add('is-revealed');
        pending.delete(section);
      }
    }
    if (!pending.size) {
      document.removeEventListener('scroll', onScroll, { capture: true });
      window.removeEventListener('resize', onScroll);
    }
  };
  const onScroll = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(check);
  };
  document.addEventListener('scroll', onScroll, { capture: true, passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  check();
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
else start();
